// ===================================
// ADMIN USERS PAGE - STAGE 7
// src/pages/AdminUsers.js
// Full user management functionality
// ===================================

import React, { useState, useEffect } from 'react';
import { 
  getUsers, 
  getUserDetails, 
  makeUserAdmin, 
  removeUserAdmin,
  resetUserPassword
} from '../services/adminApi';
import './AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, [pagination.offset]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers({
        search,
        limit: pagination.limit,
        offset: pagination.offset
      });
      setUsers(response.users || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, offset: 0 }));
    loadUsers();
  };

  const handleViewUser = async (userId) => {
    try {
      setActionLoading(true);
      const response = await getUserDetails(userId);
      setSelectedUser(response);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error loading user details:', err);
      alert('Failed to load user details');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMakeAdmin = async (userId) => {
    if (!window.confirm('Make this user an admin?')) return;
    
    try {
      setActionLoading(true);
      await makeUserAdmin(userId);
      loadUsers();
      if (selectedUser?.user?.id === userId) {
        setSelectedUser(prev => ({ ...prev, user: { ...prev.user, is_admin: true } }));
      }
    } catch (err) {
      alert('Failed to update user role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveAdmin = async (userId) => {
    if (!window.confirm('Remove admin privileges?')) return;
    
    try {
      setActionLoading(true);
      await removeUserAdmin(userId);
      loadUsers();
      if (selectedUser?.user?.id === userId) {
        setSelectedUser(prev => ({ ...prev, user: { ...prev.user, is_admin: false } }));
      }
    } catch (err) {
      alert('Failed to update user role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    try {
      setActionLoading(true);
      await resetUserPassword(selectedUser.user.id, newPassword);
      setShowPasswordModal(false);
      setNewPassword('');
      alert('Password reset successfully');
    } catch (err) {
      alert('Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <div className="header-content">
          <h1>👥 User Management</h1>
          <p>Manage platform users</p>
        </div>
        <div className="header-stats">
          <div className="stat-pill">
            <span className="stat-value">{pagination.total}</span>
            <span className="stat-label">Total Users</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by email, username, or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">Search</button>
        </form>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Users Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Sessions</th>
              <th>Avg Score</th>
              <th>Joined</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="loading-cell"><div className="spinner"></div> Loading...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="7" className="empty-cell">No users found</td></tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="user-email">
                      <span className="email-text">{user.email}</span>
                      {user.username && <span className="username">@{user.username}</span>}
                    </div>
                  </td>
                  <td>{user.name || '-'}</td>
                  <td>{user.session_count || 0}</td>
                  <td>
                    {user.avg_score ? (
                      <span className={`score-badge ${user.avg_score >= 70 ? 'good' : 'low'}`}>
                        {user.avg_score}%
                      </span>
                    ) : '-'}
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <span className={`role-badge ${user.is_admin ? 'admin' : 'user'}`}>
                      {user.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button onClick={() => handleViewUser(user.id)} className="btn btn-sm btn-secondary" title="View">👁️</button>
                      {user.is_admin ? (
                        <button onClick={() => handleRemoveAdmin(user.id)} className="btn btn-sm btn-warning" title="Remove Admin">⬇️</button>
                      ) : (
                        <button onClick={() => handleMakeAdmin(user.id)} className="btn btn-sm btn-info" title="Make Admin">⬆️</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
            disabled={pagination.offset === 0}
            className="btn btn-sm"
          >← Previous</button>
          <span className="page-info">Page {currentPage} of {totalPages}</span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
            disabled={currentPage >= totalPages}
            className="btn btn-sm"
          >Next →</button>
        </div>
      )}

      {/* User Detail Modal */}
      {showDetailModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="user-detail-grid">
                <div className="detail-section">
                  <h3>Profile</h3>
                  <div className="detail-row"><span>Email:</span><span>{selectedUser.user.email}</span></div>
                  <div className="detail-row"><span>Username:</span><span>{selectedUser.user.username || '-'}</span></div>
                  <div className="detail-row"><span>Name:</span><span>{selectedUser.user.name || '-'}</span></div>
                  <div className="detail-row"><span>Role:</span><span className={`role-badge ${selectedUser.user.is_admin ? 'admin' : 'user'}`}>{selectedUser.user.is_admin ? 'Admin' : 'User'}</span></div>
                  <div className="detail-row"><span>Joined:</span><span>{formatDate(selectedUser.user.created_at)}</span></div>
                  <div className="detail-row"><span>Last Login:</span><span>{formatDate(selectedUser.user.last_login)}</span></div>
                </div>
                <div className="detail-section">
                  <h3>Statistics</h3>
                  <div className="stats-grid-small">
                    <div className="stat-item"><span className="stat-value">{selectedUser.user.session_count || 0}</span><span className="stat-label">Sessions</span></div>
                    <div className="stat-item"><span className="stat-value">{selectedUser.user.completed_sessions || 0}</span><span className="stat-label">Completed</span></div>
                    <div className="stat-item"><span className="stat-value">{selectedUser.user.avg_score || '-'}%</span><span className="stat-label">Avg Score</span></div>
                  </div>
                </div>
              </div>

              {selectedUser.sessions?.length > 0 && (
                <div className="detail-section full-width">
                  <h3>Recent Sessions</h3>
                  <div className="mini-table">
                    <table>
                      <thead><tr><th>Type</th><th>Duration</th><th>Status</th><th>Score</th><th>Date</th></tr></thead>
                      <tbody>
                        {selectedUser.sessions.map(s => (
                          <tr key={s.id}>
                            <td className="capitalize">{s.interview_type}</td>
                            <td>{s.duration} min</td>
                            <td><span className={`status-badge ${s.status}`}>{s.status}</span></td>
                            <td>{s.score?.overall || '-'}</td>
                            <td>{formatDate(s.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button onClick={() => setShowPasswordModal(true)} className="btn btn-warning">🔑 Reset Password</button>
                {selectedUser.user.is_admin ? (
                  <button onClick={() => handleRemoveAdmin(selectedUser.user.id)} className="btn btn-danger" disabled={actionLoading}>Remove Admin</button>
                ) : (
                  <button onClick={() => handleMakeAdmin(selectedUser.user.id)} className="btn btn-primary" disabled={actionLoading}>Make Admin</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button onClick={() => setShowPasswordModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <p>Set new password for <strong>{selectedUser.user.email}</strong></p>
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  className="form-input"
                />
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowPasswordModal(false)} className="btn btn-secondary">Cancel</button>
                <button onClick={handleResetPassword} className="btn btn-primary" disabled={actionLoading || newPassword.length < 6}>
                  {actionLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
